import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SpAccount } from '../SpAccountContext';
import { API_BASE } from '../api';

interface Props { account: SpAccount; }

interface SyncLog {
  id: string; data_type: string; trigger: string; status: string;
  marketplace_id: number | null; records_fetched: number;
  error_message: string | null; started_at: string; finished_at: string | null;
  // Live-job fields (populated by the backend while a worker is still tracking the row).
  job_id: string | null;
  phase: string | null;
  message: string | null;
  chunks_total: number;
  chunks_done: number;
  current_marketplace: string | null;
  cancel_requested: boolean;
  is_orphaned: boolean;
}

const TYPE_ICONS: Record<string, string> = { orders: '🛒', inventory: '📦', inventory_reserved: '🔒', fbm_inventory: '🚚', business_reports: '📊' };
const STATUS_COLORS: Record<string, string> = {
  completed: 'var(--success)', failed: 'var(--danger)', running: 'var(--info)', cancelled: 'var(--warning)',
};
const PHASE_LABEL: Record<string, string> = {
  starting: 'Starting', creating_report: 'Requesting report',
  polling_report: 'Amazon is generating the report', downloading: 'Downloading',
  saving: 'Saving',
};

const SpSyncHistory: React.FC<Props> = ({ account }) => {
  const token = localStorage.getItem('access_token') ?? '';
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dataTypeFilter, setDataTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [cancelling, setCancelling] = useState<Record<string, boolean>>({});
  const [cleaning, setCleaning] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    const params = new URLSearchParams({ page_size: '30' });
    if (dataTypeFilter) params.set('data_type', dataTypeFilter);
    if (statusFilter) params.set('status', statusFilter);
    try {
      const res = await fetch(`${API_BASE}/api/v1/sp-api/accounts/${account.seller_id}/sync-log?${params}`, { headers });
      const data = await res.json();
      setLogs(data.data ?? []); setTotal(data.total ?? 0);
    } catch {
      // network error — keep existing data
    } finally { if (showSpinner) setLoading(false); }
  }, [account.seller_id, dataTypeFilter, statusFilter, token]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh (quietly, no spinner) while anything is running.
  const hasRunning = logs.some(l => l.status === 'running' && !l.is_orphaned);
  useEffect(() => {
    if (hasRunning) {
      pollRef.current = setInterval(() => load(false), 2000);
      return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }
  }, [hasRunning, load]);

  const cancel = async (log: SyncLog) => {
    if (!log.job_id) return;
    setCancelling(c => ({ ...c, [log.id]: true }));
    try {
      await fetch(`${API_BASE}/api/v1/sp-api/jobs/${log.job_id}/cancel`, { method: 'POST', headers });
      load(false);
    } catch {
      setCancelling(c => ({ ...c, [log.id]: false }));
    }
  };

  const cleanupStale = async () => {
    setCleaning(true);
    try {
      await fetch(`${API_BASE}/api/v1/sp-api/accounts/${account.seller_id}/sync-log/cleanup-stale`, { method: 'POST', headers });
      await load(false);
    } catch { /* ignore */ } finally { setCleaning(false); }
  };

  const duration = (log: SyncLog) => {
    const end = log.finished_at ? new Date(log.finished_at).getTime() : Date.now();
    const ms = end - new Date(log.started_at).getTime();
    const s = Math.round(ms / 1000);
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
  };

  const orphanCount = logs.filter(l => l.is_orphaned).length;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="form-input" style={{ width: 160, fontSize: 12, padding: '4px 8px' }} value={dataTypeFilter} onChange={e => setDataTypeFilter(e.target.value)}>
          <option value="">All Data Types</option>
          <option value="orders">Orders</option>
          <option value="inventory">Inventory</option>
          <option value="inventory_reserved">Inventory Reserved</option>
          <option value="fbm_inventory">FBM Inventory</option>
          <option value="business_reports">Business Reports</option>
        </select>
        <select className="form-input" style={{ width: 130, fontSize: 12, padding: '4px 8px' }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="running">Running</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => load()} disabled={loading}>Apply</button>
        <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => { setDataTypeFilter(''); setStatusFilter(''); setTimeout(() => load(), 0); }}>Reset</button>
        {hasRunning && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--info)' }}>
            <span className="spinner" style={{ width: 12, height: 12 }} /> Live · auto-refreshing
          </span>
        )}
        <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: 13 }}>{total} entries</span>
      </div>

      {/* Orphaned-rows banner */}
      {orphanCount > 0 && (
        <div className="alert" style={{
          marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
          background: 'var(--warning-bg, rgba(234,179,8,0.12))', border: '1px solid var(--warning)', color: 'var(--warning)',
        }}>
          <span>
            ⚠ {orphanCount} pull{orphanCount > 1 ? 's are' : ' is'} stuck on “running” but no longer active
            (likely a server restart). Clean them up to mark them failed.
          </span>
          <button className="btn btn-secondary btn-small" onClick={cleanupStale} disabled={cleaning}>
            {cleaning ? 'Cleaning…' : 'Clean up stale'}
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading"><div className="spinner" /> Loading…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {logs.map(log => {
            const isLive = log.status === 'running' && !log.is_orphaned;
            const isCancelling = log.cancel_requested || cancelling[log.id];
            const displayStatus = log.is_orphaned ? 'interrupted' : log.status;
            const statusColor = log.is_orphaned ? 'var(--warning)' : (STATUS_COLORS[log.status] ?? 'var(--text-muted)');
            const pct = log.chunks_total > 0
              ? Math.min(99, Math.round((log.chunks_done / log.chunks_total) * 100))
              : (log.phase === 'starting' || !log.phase ? 4 : 12);
            const mkCode = log.marketplace_id
              ? (account.marketplaces.find(m => m.id === log.marketplace_id)?.code ?? `#${log.marketplace_id}`)
              : 'All';
            return (
              <div key={log.id} className="card" style={{ padding: '14px 18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 22 }}>{TYPE_ICONS[log.data_type] ?? '🔄'}</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, textTransform: 'capitalize' }}>
                        {log.data_type.replace('_', ' ')}
                        <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 6px' }}>{mkCode}</span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                        {new Date(log.started_at).toLocaleString()} · {duration(log)} · {log.trigger}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                      {log.records_fetched} records
                    </span>
                    {isLive && (
                      <button
                        className="btn btn-small"
                        onClick={() => cancel(log)}
                        disabled={isCancelling || !log.job_id}
                        style={{
                          background: 'var(--danger-bg)', border: '1px solid var(--danger-border)',
                          color: 'var(--danger)', opacity: isCancelling ? 0.7 : 1,
                        }}
                      >
                        {isCancelling ? 'Stopping…' : '■ Stop'}
                      </button>
                    )}
                    <span style={{
                      padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                      background: statusColor + '22', color: statusColor,
                    }}>{displayStatus}</span>
                  </div>
                </div>

                {/* Live progress */}
                {isLive && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {PHASE_LABEL[log.phase ?? ''] ?? 'Working'}
                        {log.message ? ` — ${log.message}` : ''}
                      </span>
                      {log.chunks_total > 0 && (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                          {log.chunks_done}/{log.chunks_total} reports
                        </span>
                      )}
                    </div>
                    <div style={{ height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', width: `${pct}%`, borderRadius: 3,
                        background: isCancelling ? 'var(--warning)' : 'var(--accent)',
                        transition: 'width 0.4s ease',
                      }} />
                    </div>
                    {isCancelling && (
                      <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--warning)' }}>
                        Stopping after the current step — partial data will be discarded.
                      </div>
                    )}
                  </div>
                )}

                {log.error_message && (
                  <div style={{ marginTop: 8, fontSize: 12, color: 'var(--danger)', background: 'var(--danger)11', padding: '6px 10px', borderRadius: 6 }}>
                    {log.error_message}
                  </div>
                )}
              </div>
            );
          })}
          {logs.length === 0 && (
            <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              No sync history yet. Trigger a pull to get started.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SpSyncHistory;
